import type { AttendanceDayStatus, AttendanceReport, AttendanceReportDay, AttendanceReportRow } from '@erp/shared';
import { Download, Grid3x3, Printer, Table2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, DataTable, type DataTableColumn, EmptyState, Input, Skeleton } from '../../../components/ui/index.js';
import { formatMinutes, getAttendanceReport, localToday, type BranchFilter } from '../../../lib/attendance.js';

import type { BranchOption } from './AttendanceBoardView.js';

/** First and last day of a `YYYY-MM` month. */
function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/**
 * Cell colour per status in the grid; days nobody was expected stay empty.
 * Solid tokens only: the theme colours are plain CSS variables, so an opacity
 * modifier (`bg-success`) generates no rule at all — see tailwind.config.ts.
 */
const CELL: Record<AttendanceDayStatus, string> = {
  present: 'bg-success',
  absent: 'bg-danger',
  not_yet: 'bg-bg-muted',
  untracked: 'bg-transparent',
  leave: 'bg-info',
  holiday: 'bg-brand',
  off: 'bg-bg-muted',
  upcoming: 'bg-transparent',
  unscheduled: 'bg-transparent',
  not_employed: 'bg-transparent',
};

const LEGEND: AttendanceDayStatus[] = ['present', 'absent', 'leave', 'holiday', 'off'];

function cellClass(d: AttendanceReportDay): string {
  if (d.status === 'present' && (d.lateMinutes > 0 || d.earlyLeaveMinutes > 0)) return 'bg-warning';
  return CELL[d.status];
}

/** A CSV the shop can open in Excel — BOM first, or Excel reads Arabic names as mojibake. */
function toCsv(rows: string[][]): string {
  const esc = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return `\uFEFF${rows.map((r) => r.map(esc).join(',')).join('\r\n')}`;
}

/**
 * The month (or any range up to a quarter) per employee: the totals payroll
 * will read, and a grid of days so a pattern — every Saturday late, every
 * Thursday gone early — is visible at a glance.
 */
export function AttendanceReportView({ branches }: { branches: BranchOption[] }): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const [month, setMonth] = useState(() => localToday().slice(0, 7));
  const [branch, setBranch] = useState<BranchFilter>(undefined);
  const [view, setView] = useState<'totals' | 'grid'>('totals');
  const [report, setReport] = useState<AttendanceReport | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    setLoading(true);
    try {
      const { from, to } = monthRange(month);
      setReport(await getAttendanceReport(from, to, { branch }));
    } catch {
      toast.error(t('attendance.errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [month, branch, t]);

  useEffect(() => { void load(); }, [load]);

  const columns: DataTableColumn<AttendanceReportRow>[] = useMemo(() => {
    const mins = (id: string, header: string, pick: (r: AttendanceReportRow) => number, tone?: string): DataTableColumn<AttendanceReportRow> => ({
      id,
      header,
      align: 'end',
      sortValue: pick,
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => (pick(r) > 0 ? <span className={tone}>{formatMinutes(pick(r))}</span> : '—'),
    });
    const count = (id: string, header: string, pick: (r: AttendanceReportRow) => number, tone?: string): DataTableColumn<AttendanceReportRow> => ({
      id,
      header,
      align: 'end',
      sortValue: pick,
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => (pick(r) > 0 ? <span className={tone}>{pick(r)}</span> : '—'),
    });
    return [
      {
        id: 'employee',
        header: t('attendance.columns.employee'),
        sortValue: (r) => r.employee.displayName,
        searchValue: (r) => `${r.employee.code} ${r.employee.displayName}`,
        cell: (r) => (
          <span>
            <span className="font-mono text-xs text-fg-muted">{r.employee.code}</span>{' '}
            <span className="font-medium">{r.employee.displayName}</span>
          </span>
        ),
      },
      count('working', t('attendance.report.workingDays'), (r) => r.totals.workingDays),
      count('present', t('attendance.summary.present'), (r) => r.totals.presentDays, 'text-success'),
      count('absent', t('attendance.summary.absent'), (r) => r.totals.absentDays, 'text-danger'),
      count('leave', t('attendance.summary.leave'), (r) => r.totals.leaveDays, 'text-info'),
      count('unpaid', t('attendance.report.unpaidLeave'), (r) => r.totals.unpaidLeaveDays, 'text-warning'),
      count('lateDays', t('attendance.report.lateDays'), (r) => r.totals.lateDays, 'text-warning'),
      mins('late', t('attendance.report.lateTime'), (r) => r.totals.lateMinutes, 'text-warning'),
      mins('early', t('attendance.columns.early'), (r) => r.totals.earlyLeaveMinutes + r.totals.missedMinutes, 'text-warning'),
      mins('worked', t('attendance.columns.worked'), (r) => r.totals.workedMinutes),
      mins('overtime', t('attendance.columns.overtime'), (r) => r.totals.overtimeMinutes, 'text-success'),
      count('missing', t('attendance.summary.missingCheckout'), (r) => r.totals.missingCheckouts, 'text-danger'),
    ];
  }, [t]);

  const exportCsv = (): void => {
    if (!report) return;
    const head = [
      t('attendance.columns.employee'), 'code',
      t('attendance.report.workingDays'), t('attendance.summary.present'), t('attendance.summary.absent'),
      t('attendance.summary.leave'), t('attendance.report.unpaidLeave'), t('attendance.report.lateDays'),
      t('attendance.report.lateTime'), t('attendance.columns.early'), t('attendance.columns.worked'),
      t('attendance.columns.overtime'), t('attendance.summary.missingCheckout'),
    ];
    const body = report.rows.map((r) => [
      r.employee.displayName, r.employee.code,
      String(r.totals.workingDays), String(r.totals.presentDays), String(r.totals.absentDays),
      String(r.totals.leaveDays), String(r.totals.unpaidLeaveDays), String(r.totals.lateDays),
      formatMinutes(r.totals.lateMinutes), formatMinutes(r.totals.earlyLeaveMinutes + r.totals.missedMinutes),
      formatMinutes(r.totals.workedMinutes), formatMinutes(r.totals.overtimeMinutes), String(r.totals.missingCheckouts),
    ]);
    const blob = new Blob([toCsv([head, ...body])], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${report.from}-${report.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dates = report?.rows[0]?.days.map((d) => d.date) ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-2 print:hidden">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{t('attendance.report.month')}</span>
          <div className="w-44">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
        </label>
        {branches.length > 1 && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">{tc('filters.branch', { defaultValue: 'Branch' })}</span>
            <select
              value={branch ?? ''}
              onChange={(e) => setBranch(e.target.value || undefined)}
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
            >
              <option value="">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              <option value="none">{t('attendance.board.noBranch')}</option>
            </select>
          </label>
        )}
        <div className="flex rounded-md border border-border p-0.5">
          <Button size="sm" variant={view === 'totals' ? 'secondary' : 'ghost'} onClick={() => setView('totals')} className="gap-1.5">
            <Table2 className="h-4 w-4" aria-hidden />
            {t('attendance.report.totals')}
          </Button>
          <Button size="sm" variant={view === 'grid' ? 'secondary' : 'ghost'} onClick={() => setView('grid')} className="gap-1.5">
            <Grid3x3 className="h-4 w-4" aria-hidden />
            {t('attendance.report.grid')}
          </Button>
        </div>
        <div className="ms-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={exportCsv} disabled={!report || report.rows.length === 0} className="gap-1.5">
            <Download className="h-4 w-4" aria-hidden />
            {t('attendance.report.csv')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => window.print()} className="gap-1.5">
            <Printer className="h-4 w-4" aria-hidden />
            {t('attendance.report.print')}
          </Button>
        </div>
      </div>

      {report && (
        <p className="text-xs text-fg-muted">
          <span dir="ltr">{report.from} – {report.to}</span> · {report.timezone}
        </p>
      )}

      {loading && !report ? (
        <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>
      ) : report && report.rows.length === 0 ? (
        <EmptyState title={t('attendance.board.empty')} description={t('attendance.board.emptyDescription')} />
      ) : report && view === 'totals' ? (
        <Card className={`overflow-hidden p-0 ${loading ? 'opacity-60' : ''}`}>
          <DataTable
            columns={columns}
            data={report.rows}
            rowKey={(r) => r.employee.id}
            pageSize={100}
            searchPlaceholder={t('attendance.board.search')}
          />
        </Card>
      ) : report ? (
        <Card className={`overflow-x-auto p-3 ${loading ? 'opacity-60' : ''}`}>
          <table className="border-separate border-spacing-0.5 text-xs">
            <thead>
              <tr>
                <th className="sticky start-0 z-10 bg-bg px-2 text-start font-medium text-fg-muted">{t('attendance.columns.employee')}</th>
                {dates.map((d) => (
                  <th key={d} className={`w-6 text-center font-mono font-normal ${d === report.today ? 'text-brand' : 'text-fg-muted'}`}>
                    {Number(d.slice(8))}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.employee.id}>
                  <td className="sticky start-0 z-10 whitespace-nowrap bg-bg px-2 py-0.5">
                    <span className="font-mono text-fg-muted">{r.employee.code}</span> {r.employee.displayName}
                  </td>
                  {r.days.map((d) => (
                    <td key={d.date} className="p-0">
                      <div
                        className={`h-5 w-6 rounded-sm ${cellClass(d)} ${d.missingCheckout ? 'ring-2 ring-danger ring-inset' : ''}`}
                        title={[
                          d.date,
                          t(`attendance.status.${d.status}`),
                          d.lateMinutes > 0 ? `${t('attendance.summary.late')} ${formatMinutes(d.lateMinutes)}` : null,
                          d.earlyLeaveMinutes > 0 ? `${t('attendance.columns.early')} ${formatMinutes(d.earlyLeaveMinutes)}` : null,
                          d.workedMinutes > 0 ? `${t('attendance.columns.worked')} ${formatMinutes(d.workedMinutes)}` : null,
                          d.missingCheckout ? t('attendance.summary.missingCheckout') : null,
                        ].filter(Boolean).join(' · ')}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-fg-muted">
            {LEGEND.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5">
                <span className={`inline-block h-3 w-3 rounded-sm ${CELL[s]}`} />
                {t(`attendance.status.${s}`)}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-warning" />
              {t('attendance.report.lateOrEarly')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm ring-2 ring-inset ring-danger" />
              {t('attendance.summary.missingCheckout')}
            </span>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
